<template>
  <!--
    Three reason elements, always: which field matched, which word, which party it came
    from. There is deliberately no match-kind label — a row says which field, never how.
  -->
  <LlListItem
    clickable
    data-cy="conflict-result-row"
    class="border-b border-gray-200 last:border-b-0"
    :class="{ 'border-l-[3px] border-l-gray-300': grouped }"
    @click="emit('open', row)"
  >
    <div class="grid gap-1 whitespace-normal py-1">
      <!--
        No flex-wrap: a long client-matter name has to wrap inside its own box and
        leave the pill on the first line, otherwise the pill drops below and the
        right-hand column of matched fields stops lining up.
      -->
      <span class="flex items-start gap-2.5">
        <span class="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">
          {{ row.recordLabel }}
          <span
            v-if="row.archived"
            class="ml-1.5 whitespace-nowrap rounded-sm border border-gray-300 px-1.5 text-xs uppercase tracking-wide text-gray-500"
            data-cy="conflict-archived-tag"
          >
            Archived
          </span>
        </span>
        <!--
          Pushed right so the matched fields form a column you can scan down. A row
          can match on more than one field — a client whose name and company name
          both hit — so this is a list, not a single pill.
        -->
        <span
          class="ml-auto flex flex-none flex-wrap justify-end gap-1"
          data-cy="conflict-matched-fields"
        >
          <ConflictFieldPill
            v-for="field in row.fields"
            :key="field.id"
            :field="field.id"
          />
        </span>
      </span>

      <!--
        One line per distinct value. Two fields holding the same string — a client whose
        name and company name agree — are one reason to a reviewer, so they share a line
        and both pills above; an email that also matched is a different string and gets
        its own.
      -->
      <span
        v-for="line in valueLines"
        :key="line.value"
        class="text-xs text-gray-700 [overflow-wrap:anywhere]"
      >
        <!-- ll-standards-ignore LL091 -- static list: tokens are derived from one
             field value and re-derived whole whenever it changes -->
        <template v-for="(token, index) in line.tokens" :key="index">
          <mark
            v-if="token.matched"
            class="bg-yellow-200 px-0.5 text-yellow-900"
            data-cy="conflict-matched-word"
            >{{ token.text }}</mark
          >
          <template v-else>{{ token.text }}</template>
        </template>
      </span>

      <span class="flex flex-wrap gap-3 text-xs font-normal text-gray-500">
        <span data-cy="conflict-from-party">
          From <span class="font-medium text-gray-700">{{ row.party }}</span>
        </span>
        <span v-if="row.clientLabel">Client · {{ row.clientLabel }}</span>
        <span v-if="showFraction" class="font-medium text-amber-700">
          {{ row.matchedWordCount }} of {{ row.partyWordCount }} words
        </span>
      </span>
    </div>
  </LlListItem>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlListItem from '@/components/LlListItem.vue';
import ConflictFieldPill from './ConflictFieldPill.vue';
import { highlightTokens, truncate } from '../utils/conflictHighlightHelpers';
import type { ConflictResultRow } from '../models/conflictTypes';

const props = defineProps<{
  row: ConflictResultRow;
  /** Partial rows say how much of the name matched; full rows have nothing to add. */
  showFraction?: boolean;
  /** Second and later row for the same record, indented rather than repeated. */
  grouped?: boolean;
}>();

const emit = defineEmits<{ open: [row: ConflictResultRow] }>();

const valueLines = computed(() => {
  const byValue = new Map<string, string[]>();

  props.row.fields.forEach((field) => {
    const value = field.value ?? '';
    byValue.set(value, (byValue.get(value) ?? []).concat(field.matchedWords));
  });

  return Array.from(byValue, ([value, matchedWords]) => ({
    value,
    tokens: highlightTokens(truncate(value), matchedWords),
  }));
});
</script>
